import { useState, useRef, useCallback, useEffect } from 'react';
import JsSIP from 'jssip';
import { fetchSoftphoneConfig } from '../services/softphoneService';
import { cagriSonlandir as netgsmCagriSonlandir } from '../services/santralYonetimService';
import { normalizeTurkishPhone } from '../utils/phoneNormalize';
import { formatNetgsmSipHata, sipHataDebugOzet } from '../utils/netgsmSipErrors';

JsSIP.debug.disable('JsSIP:*');

const DEFAULT_PC_CONFIG = {
  iceServers: [
    { urls: 'stun:stun.netgsm.com.tr:3478' },
    { urls: 'stun:stun.l.google.com:19302' },
    { urls: 'stun:stun1.l.google.com:19302' },
  ],
  iceTransportPolicy: 'all',
};

const formatCaller = (session) => {
  try {
    const identity = session?.remote_identity;
    const display = identity?.display_name;
    const user = identity?.uri?.user;
    if (display && display !== user) return display;
    return user || 'Bilinmeyen numara';
  } catch {
    return 'Bilinmeyen numara';
  }
};

const formatPhone = (session) => {
  try {
    return session?.remote_identity?.uri?.user || '—';
  } catch {
    return '—';
  }
};

/** JsSIP oturumu hâlâ canlı mı (bitmiş ref takılı kalmış olabilir) */
const sessionAktifMi = (session) => {
  if (!session) return false;
  try {
    if (session.isEnded?.()) return false;
    return Boolean(session.isInProgress?.() || session.isEstablished?.());
  } catch {
    return false;
  }
};

const ensureMicTracksEnabled = (stream) => {
  stream?.getAudioTracks?.().forEach((track) => {
    track.enabled = true;
  });
};

const releaseMediaStream = (stream) => {
  stream?.getTracks?.().forEach((track) => track.stop());
};

/** STUN/TURN varken JsSIP ICE gathering uzun sürebilir — srflx/relay gelince veya en geç 6 sn sonra devam et. */
const bindIceCandidateEarlyReady = (session) => {
  let maxWaitTimer = null;
  let resolved = false;

  const safeReady = (ready) => {
    if (resolved || typeof ready !== 'function') return;
    resolved = true;
    if (maxWaitTimer) {
      clearTimeout(maxWaitTimer);
      maxWaitTimer = null;
    }
    ready();
  };

  session.on('icecandidate', (event) => {
    const { candidate, ready } = event;
    if (!candidate || typeof ready !== 'function') return;

    if (!maxWaitTimer) {
      maxWaitTimer = setTimeout(() => safeReady(ready), 6000);
    }

    const candStr = candidate.candidate || '';
    const isSrflx = /\btyp srflx\b/i.test(candStr);
    const isRelay = /\btyp relay\b/i.test(candStr);

    if (isSrflx || isRelay) {
      safeReady(ready);
    }
  });
};

const getAnswerOptions = (pcConfig, mediaStream) => {
  return {
    mediaConstraints: { audio: true, video: false },
    pcConfig,
    mediaStream,
  };
};

const hasActiveAudioSender = (pc) => {
  if (!pc) return false;
  return pc.getSenders().some(
    (sender) => sender.track?.kind === 'audio'
      && sender.track.readyState === 'live'
      && sender.track.enabled
  );
};

/** Netgsm hangup için SIP INVITE / oturumdan unique_id çıkarır */
const extractNetgsmUniqueId = (session) => {
  if (!session) return null;
  try {
    const req = session.request;
    const headerNames = [
      'X-Uniqueid',
      'X-unique-id',
      'X-Asterisk-Uniqueid',
      'Unique-ID',
      'X-Netgsm-Unique-Id',
      'X-Channel',
    ];
    for (const name of headerNames) {
      const val = req?.getHeader?.(name);
      if (val && String(val).trim()) return String(val).trim();
    }
    const headers = req?.headers;
    if (headers && typeof headers === 'object') {
      for (const [key, val] of Object.entries(headers)) {
        if (!/unique|channel/i.test(key)) continue;
        const raw = Array.isArray(val) ? val[0]?.raw ?? val[0] : val;
        if (raw && String(raw).trim()) return String(raw).trim();
      }
    }
  } catch {
    /* ignore */
  }
  return null;
};

const RINGTONE_SRC = `${process.env.PUBLIC_URL || ''}/sounds/bell.mp3`;

/** Gelen çağrı zil sesi — frontend/public/sounds/bell.mp3 */
function useRingtone(ringtoneAudioRef) {
  const unlockedRef = useRef(false);

  const getAudio = useCallback(() => ringtoneAudioRef.current, [ringtoneAudioRef]);

  /** Kullanıcı tıklaması sonrası tarayıcı ses kilidini açar (startSoftphone). */
  const prime = useCallback(async () => {
    const audio = getAudio();
    if (!audio) return;
    try {
      if (!audio.src || audio.src.endsWith('/')) {
        audio.src = RINGTONE_SRC;
      }
      audio.loop = true;
      audio.preload = 'auto';
      const prevVolume = audio.volume;
      audio.volume = 0.001;
      audio.currentTime = 0;
      await audio.play();
      audio.pause();
      audio.currentTime = 0;
      audio.volume = prevVolume || 1;
      unlockedRef.current = true;
    } catch (err) {
      console.warn('[santral zil] hazırlık (prime) başarısız:', err);
    }
  }, [getAudio]);

  const stop = useCallback(() => {
    const audio = getAudio();
    if (!audio) return;
    audio.pause();
    audio.currentTime = 0;
  }, [getAudio]);

  const start = useCallback(async () => {
    const audio = getAudio();
    if (!audio) return;
    try {
      if (!audio.src || audio.src.endsWith('/')) {
        audio.src = RINGTONE_SRC;
      }
      audio.loop = true;
      audio.volume = 1;
      if (audio.paused) {
        audio.currentTime = 0;
        await audio.play();
      }
    } catch (err) {
      console.warn('[santral zil] oynatılamadı:', err);
      if (typeof window !== 'undefined' && window.showNotification) {
        window.showNotification('warning', 'Zil sesi çalınamadı. Sayfayı yenileyip santrali tekrar başlatın.');
      }
    }
  }, [getAudio]);

  useEffect(() => () => stop(), [stop]);

  return { start, stop, prime, unlockedRef };
}

const MAX_SANTRAL_LOG = 100;
const MAX_GELEN_CAGRI = 80;

/**
 * Netgsm/Netsantral JsSIP WebRTC softphone — yalnızca gelen çağrı (ilk aşama).
 */
export function useNetgsmSoftphone() {
  const [connectionState, setConnectionState] = useState('idle');
  const [statusMessage, setStatusMessage] = useState('');
  const [incomingCall, setIncomingCall] = useState(null);
  const [activeCall, setActiveCall] = useState(null);
  const [extensionLabel, setExtensionLabel] = useState('');
  const [micActive, setMicActive] = useState(false);
  const [micMuted, setMicMuted] = useState(false);
  const [isAnswering, setIsAnswering] = useState(false);
  const [isTransferring, setIsTransferring] = useState(false);
  const [isDialing, setIsDialing] = useState(false);
  const [santralLoglari, setSantralLoglari] = useState([]);
  const [gelenCagriGecmisi, setGelenCagriGecmisi] = useState([]);

  const uaRef = useRef(null);
  const sessionRef = useRef(null);
  const remoteAudioRef = useRef(null);
  const ringtoneAudioRef = useRef(null);
  const localMediaStreamRef = useRef(null);
  const pcConfigRef = useRef(DEFAULT_PC_CONFIG);
  const mountedRef = useRef(true);
  const isAnsweringRef = useRef(false);
  const answerTimeoutRef = useRef(null);
  const onCallAcceptedRef = useRef(null);
  const onCallEndedRef = useRef(null);
  const lastCallMetaRef = useRef(null);
  const sipDomainRef = useRef('');
  const outboundTelefonRef = useRef(null);
  const outboundTimeoutRef = useRef(null);
  const santralLogIdRef = useRef(0);
  const gelenCagriIdRef = useRef(0);
  const ringtone = useRingtone(ringtoneAudioRef);

  const appendSantralLog = useCallback((mesaj, seviye = 'info') => {
    if (!mesaj) return;
    const id = ++santralLogIdRef.current;
    setSantralLoglari((prev) => [
      { id, mesaj, seviye, zaman: Date.now() },
      ...prev,
    ].slice(0, MAX_SANTRAL_LOG));
  }, []);

  const clearSantralLoglari = useCallback(() => {
    setSantralLoglari([]);
  }, []);

  const kaydetGelenCagri = useCallback((telefon) => {
    const normalized = String(telefon || '').replace(/\D/g, '');
    if (!normalized) return;
    const now = Date.now();
    setGelenCagriGecmisi((prev) => {
      const dup = prev.find(
        (g) => g.telefon === normalized && now - g.zaman < 60000
      );
      if (dup) return prev;
      const id = ++gelenCagriIdRef.current;
      return [{ id, telefon: normalized, zaman: now }, ...prev].slice(0, MAX_GELEN_CAGRI);
    });
  }, []);

  const clearOutboundTimeout = useCallback(() => {
    if (outboundTimeoutRef.current) {
      clearTimeout(outboundTimeoutRef.current);
      outboundTimeoutRef.current = null;
    }
  }, []);

  const bilgilendirSantral = useCallback((mesaj, { seviye = 'error' } = {}) => {
    appendSantralLog(mesaj, seviye);
  }, [appendSantralLog]);

  const setOnCallAccepted = useCallback((fn) => {
    onCallAcceptedRef.current = typeof fn === 'function' ? fn : null;
  }, []);

  const setOnCallEnded = useCallback((fn) => {
    onCallEndedRef.current = typeof fn === 'function' ? fn : null;
  }, []);

  const hasLiveMicStream = (stream) =>
    stream?.active && stream.getAudioTracks().some((t) => t.readyState === 'live');

  const resetAnswering = useCallback(() => {
    if (answerTimeoutRef.current) {
      clearTimeout(answerTimeoutRef.current);
      answerTimeoutRef.current = null;
    }
    isAnsweringRef.current = false;
    setIsAnswering(false);
  }, []);

  const playRemoteStream = useCallback((stream) => {
    if (!remoteAudioRef.current || !stream) return;
    remoteAudioRef.current.srcObject = stream;
    remoteAudioRef.current.play().catch(() => {});
  }, []);

  const attachRemoteAudioToPc = useCallback((pc) => {
    if (!pc) return;

    pc.addEventListener('track', (event) => {
      if (event.track?.kind !== 'audio') return;
      if (event.streams?.[0]) {
        playRemoteStream(event.streams[0]);
      } else {
        playRemoteStream(new MediaStream([event.track]));
      }
    });

    pc.getReceivers().forEach((receiver) => {
      if (receiver.track?.kind === 'audio') {
        playRemoteStream(new MediaStream([receiver.track]));
      }
    });
  }, [playRemoteStream]);

  const attachRemoteAudio = useCallback((session) => {
    attachRemoteAudioToPc(session?.connection);
  }, [attachRemoteAudioToPc]);

  /** SantralPanel remount veya kalıcı audio element bağlandığında uzak sesi yeniden bağla */
  const reattachRemoteAudio = useCallback(() => {
    const session = sessionRef.current;
    const pc = session?.connection;
    if (!pc || !remoteAudioRef.current) return;
    pc.getReceivers().forEach((receiver) => {
      if (receiver.track?.kind === 'audio' && receiver.track.readyState === 'live') {
        playRemoteStream(new MediaStream([receiver.track]));
      }
    });
  }, [playRemoteStream]);

  const acquireLocalMic = useCallback(async () => {
    let stream = localMediaStreamRef.current;
    if (hasLiveMicStream(stream)) {
      ensureMicTracksEnabled(stream);
      setMicActive(true);
      return stream;
    }
    releaseMediaStream(stream);
    stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
      },
      video: false,
    });
    localMediaStreamRef.current = stream;
    ensureMicTracksEnabled(stream);
    setMicActive(true);
    return stream;
  }, []);

  const releaseLocalMic = useCallback(() => {
    releaseMediaStream(localMediaStreamRef.current);
    localMediaStreamRef.current = null;
    setMicActive(false);
  }, []);

  const getSessionAnswerOptions = useCallback((mediaStream) => {
    return getAnswerOptions(pcConfigRef.current, mediaStream);
  }, []);

  const ensureLocalAudioOnSession = useCallback(async (session) => {
    const pc = session?.connection;
    if (!pc) return false;

    if (hasActiveAudioSender(pc)) {
      return true;
    }

    try {
      const stream = await acquireLocalMic();
      const audioTrack = stream.getAudioTracks()[0];
      if (!audioTrack) return false;

      const audioSender = pc.getSenders().find((sender) => sender.track?.kind === 'audio');
      if (audioSender) {
        await audioSender.replaceTrack(audioTrack);
      } else {
        pc.addTrack(audioTrack, stream);
      }

      if (session.isEstablished?.() || session.isInProgress?.()) {
        session.renegotiate({
          rtcOfferConstraints: { offerToReceiveAudio: true, offerToReceiveVideo: false },
        });
      }
      return true;
    } catch {
      return false;
    }
  }, [acquireLocalMic]);

  const clearSession = useCallback(() => {
    if (lastCallMetaRef.current && onCallEndedRef.current) {
      try {
        onCallEndedRef.current({ ...lastCallMetaRef.current });
      } catch {
        /* ignore callback errors */
      }
    }
    lastCallMetaRef.current = null;
    outboundTelefonRef.current = null;
    clearOutboundTimeout();
    setIsDialing(false);
    sessionRef.current = null;
    setIncomingCall(null);
    setActiveCall(null);
    setMicMuted(false);
    resetAnswering();
    setIsTransferring(false);
    ringtone.stop();
    if (remoteAudioRef.current) {
      remoteAudioRef.current.srcObject = null;
    }
    setConnectionState(uaRef.current?.isRegistered?.() ? 'registered' : 'idle');
  }, [clearOutboundTimeout, resetAnswering, ringtone]);

  const bindSessionEvents = useCallback((session) => {
    bindIceCandidateEarlyReady(session);

    session.on('peerconnection', (data) => {
      attachRemoteAudioToPc(data.peerconnection);
    });

    session.on('connecting', () => {
      setStatusMessage('Ses bağlantısı kuruluyor…');
    });

    session.on('getusermediafailed', () => {
      resetAnswering();
      setConnectionState('incoming');
      setStatusMessage('Mikrofon izni alınamadı. Tarayıcı izinlerini kontrol edin.');
    });

    session.on('peerconnection:setremotedescriptionfailed', () => {
      resetAnswering();
      setConnectionState('incoming');
      setStatusMessage('Ses oturumu kurulamadı. Tekrar cevaplamayı deneyin.');
    });

    session.on('peerconnection:createanswerfailed', () => {
      resetAnswering();
      setConnectionState('incoming');
      setStatusMessage('Ses yanıtı oluşturulamadı. Tekrar cevaplamayı deneyin.');
    });

    session.on('progress', () => {
      if (session.direction === 'incoming') {
        ringtone.start();
      } else {
        setIsDialing(false);
        setStatusMessage('Karşı taraf çağrılıyor…');
      }
    });

    session.on('accepted', async () => {
      clearOutboundTimeout();
      resetAnswering();
      ringtone.stop();
      setIsDialing(false);
      const yon = session.direction === 'outgoing' ? 'giden' : 'gelen';
      const telefon = yon === 'giden'
        ? (outboundTelefonRef.current || formatPhone(session))
        : formatPhone(session);
      const sipCallId = session.id;
      lastCallMetaRef.current = { telefon, sipCallId, yon };
      setIncomingCall(null);
      setActiveCall({
        id: sipCallId,
        ad: yon === 'giden' ? telefon : formatCaller(session),
        telefon,
        yon,
        baslangic: new Date().toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' }),
      });
      setConnectionState('active');
      attachRemoteAudio(session);

      if (onCallAcceptedRef.current) {
        try {
          onCallAcceptedRef.current({ telefon, sipCallId, yon, session });
        } catch {
          /* ignore callback errors */
        }
      }

      const micOk = await ensureLocalAudioOnSession(session);
      const senders = session.connection?.getSenders?.() || [];
      const hasMicSender = hasActiveAudioSender(session.connection) || senders.some(
        (s) => s.track?.kind === 'audio' && s.track.enabled
      );

      if (!hasMicSender && !micOk) {
        setStatusMessage('Uyarı: Mikrofon RTP akışı başlatılamadı. Bağlantıyı kesip tekrar deneyin.');
      } else {
        setMicActive(true);
        setMicMuted(false);
        setStatusMessage('Görüşme aktif — mikrofon açık');
      }
    });

    session.on('confirmed', () => {
      attachRemoteAudio(session);
      ensureLocalAudioOnSession(session).then((ok) => {
        if (ok) setMicActive(true);
      });
    });

    session.on('muted', (data) => {
      if (data?.audio) {
        setMicMuted(true);
        setStatusMessage('Mikrofon susturuldu');
      }
    });

    session.on('unmuted', (data) => {
      if (data?.audio) {
        setMicMuted(false);
        setStatusMessage('Mikrofon açıldı');
      }
    });

    session.on('ended', () => {
      clearOutboundTimeout();
      clearSession();
    });
    session.on('failed', (data) => {
      clearOutboundTimeout();
      resetAnswering();
      setIsDialing(false);
      outboundTelefonRef.current = null;
      const giden = session.direction === 'outgoing';
      console.warn('[Netgsm SIP failed]', sipHataDebugOzet(data));
      bilgilendirSantral(formatNetgsmSipHata(data, { giden }));
      clearSession();
    });
  }, [attachRemoteAudio, attachRemoteAudioToPc, bilgilendirSantral, clearOutboundTimeout, clearSession, ensureLocalAudioOnSession, resetAnswering, ringtone]);

  const handleOutgoingSession = useCallback((session) => {
    const mevcut = sessionRef.current;
    if (mevcut && mevcut !== session) {
      if (sessionAktifMi(mevcut)) {
        try { session.terminate(); } catch { /* ignore */ }
        return;
      }
      sessionRef.current = null;
    }

    sessionRef.current = session;
    bindSessionEvents(session);
    resetAnswering();
    setIsDialing(false);

    const telefon = outboundTelefonRef.current || formatPhone(session);
    setIncomingCall(null);
    setActiveCall({
      id: session.id,
      ad: telefon,
      telefon,
      yon: 'giden',
      baslangic: new Date().toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' }),
    });
    setConnectionState('active');
    setStatusMessage('Aranıyor…');

    if (session.direction === 'outgoing') {
      clearOutboundTimeout();
      outboundTimeoutRef.current = setTimeout(() => {
        const s = sessionRef.current;
        if (!s || s !== session || !sessionAktifMi(s) || s.isEstablished?.()) return;
        try {
          s.terminate();
        } catch {
          /* ignore */
        }
        bilgilendirSantral(
          'Netgsm: Karşı taraf yanıt vermedi (zaman aşımı). Bakiye, numara formatı veya hat durumunu kontrol edin.',
        );
      }, 45000);
    }
  }, [bindSessionEvents, bilgilendirSantral, clearOutboundTimeout, resetAnswering]);

  const handleIncomingSession = useCallback((session) => {
    const mevcut = sessionRef.current;
    if (mevcut && mevcut !== session) {
      if (sessionAktifMi(mevcut)) {
        try { session.terminate(); } catch { /* ignore */ }
        return;
      }
      sessionRef.current = null;
    }

    sessionRef.current = session;
    bindSessionEvents(session);
    resetAnswering();

    setIncomingCall({
      id: session.id,
      ad: formatCaller(session),
      telefon: formatPhone(session),
      uniqueId: extractNetgsmUniqueId(session),
      session,
    });
    kaydetGelenCagri(formatPhone(session));
    setConnectionState('incoming');
    ringtone.start();

    acquireLocalMic().catch(() => {
      /* Cevap anında JsSIP kendi getUserMedia çağırır */
    });
  }, [acquireLocalMic, bindSessionEvents, kaydetGelenCagri, resetAnswering, ringtone]);

  const stopSoftphone = useCallback(() => {
    ringtone.stop();
    if (sessionRef.current) {
      try {
        sessionRef.current.terminate();
      } catch {
        /* ignore */
      }
      sessionRef.current = null;
    }
    if (uaRef.current) {
      try {
        uaRef.current.stop();
      } catch {
        /* ignore */
      }
      uaRef.current = null;
    }
    releaseLocalMic();
    setIncomingCall(null);
    setActiveCall(null);
    setMicMuted(false);
    setConnectionState('idle');
    setStatusMessage('');
    setExtensionLabel('');
  }, [releaseLocalMic, ringtone]);

  const destroyUa = useCallback(() => {
    if (uaRef.current) {
      try {
        uaRef.current.stop();
      } catch {
        /* ignore */
      }
      uaRef.current = null;
    }
  }, []);

  const startSoftphone = useCallback(async () => {
    if (connectionState === 'starting' || connectionState === 'registering') return;

    setConnectionState('starting');
    setStatusMessage('Yapılandırma alınıyor…');

    try {
      const config = await fetchSoftphoneConfig();
      if (!config?.enabled) {
        throw new Error(
          config?.mesaj
            || `Softphone yapılandırması etkin değil${config?.eksikAlanlar?.length ? ` (eksik: ${config.eksikAlanlar.join(', ')})` : ''}.`
        );
      }

      pcConfigRef.current = {
        iceServers:
          Array.isArray(config.iceServers) && config.iceServers.length > 0
            ? config.iceServers
            : DEFAULT_PC_CONFIG.iceServers,
        iceTransportPolicy: 'all',
      };

      setStatusMessage('Mikrofon izni isteniyor…');
      await acquireLocalMic();
      await ringtone.prime();

      setConnectionState('registering');
      setStatusMessage('Santrale bağlanılıyor…');
      setExtensionLabel(config.sipExtension || config.displayName || '');
      sipDomainRef.current = config.sipDomain || '';

      destroyUa();

      const socket = new JsSIP.WebSocketInterface(config.wssUrl);
      const configuration = {
        sockets: [socket],
        uri: `sip:${config.sipUsername}@${config.sipDomain}`,
        password: config.sipPassword,
        display_name: config.displayName,
        register: true,
        session_timers: false,
      };

      if (process.env.NODE_ENV === 'development') {
        configuration.traceSip = true;
      }

      const ua = new JsSIP.UA(configuration);
      uaRef.current = ua;

      ua.on('connected', () => {
        if (uaRef.current !== ua) return;
        setStatusMessage('WebSocket bağlandı, kayıt olunuyor…');
      });

      ua.on('disconnected', (data) => {
        if (uaRef.current !== ua) return;
        const cause = data?.error ? String(data.error) : 'Bağlantı kapandı';
        setConnectionState('error');
        setStatusMessage(
          `WebSocket bağlantısı kurulamadı (${cause}). Netsantral'da dahili bağlantı tipinin WSS olduğunu kontrol edin.`
        );
        uaRef.current = null;
      });

      ua.on('registered', () => {
        setConnectionState('registered');
        setStatusMessage(`Dahili ${config.sipExtension || config.displayName} kayıtlı — mikrofon hazır`);
      });

      ua.on('unregistered', () => {
        if (uaRef.current === ua) {
          setConnectionState('idle');
          setStatusMessage('Kayıt sonlandırıldı');
        }
      });

      ua.on('registrationFailed', (data) => {
        const mesaj = formatNetgsmSipHata(
          { cause: data?.cause, response: data?.response },
          { giden: false },
        );
        setConnectionState('error');
        bilgilendirSantral(`SIP kayıt hatası: ${mesaj}`);
        destroyUa();
      });

      ua.on('newRTCSession', (data) => {
        const { session, originator } = data;
        if (originator === 'local' || session?.direction === 'outgoing') {
          handleOutgoingSession(session);
          return;
        }
        if (originator === 'remote' || session?.direction === 'incoming') {
          handleIncomingSession(session);
        }
      });

      ua.start();
    } catch (err) {
      setConnectionState('error');
      setStatusMessage(err?.message || 'Çağrı sistemi başlatılamadı');
      uaRef.current = null;
    }
  }, [acquireLocalMic, bilgilendirSantral, connectionState, destroyUa, handleIncomingSession, handleOutgoingSession, ringtone]);

  const dialCall = useCallback(async (telefonHam) => {
    const ua = uaRef.current;

    const hataDon = (mesaj, { seviye = 'error' } = {}) => {
      bilgilendirSantral(mesaj, { seviye });
      return { ok: false, mesaj };
    };

    if (!ua?.isRegistered?.()) {
      return hataDon('Santral bağlantısı gerekli — önce softphone kayıt olun.');
    }

    const mevcutSession = sessionRef.current;
    if (mevcutSession) {
      if (sessionAktifMi(mevcutSession)) {
        return hataDon('Zaten aktif bir çağrı var.');
      }
      sessionRef.current = null;
    }

    const tel = normalizeTurkishPhone(telefonHam);
    if (tel.length !== 10) {
      return hataDon('Geçerli 10 haneli telefon numarası girin.');
    }

    const domain = sipDomainRef.current;
    if (!domain) {
      return hataDon('SIP domain yapılandırması eksik.');
    }

    const prefix = (process.env.REACT_APP_NETGSM_OUTBOUND_DIAL_PREFIX || '90').replace(/\D/g, '');
    const target = `sip:${prefix}${tel}@${domain}`;

    try {
      setIsDialing(true);
      outboundTelefonRef.current = tel;
      setStatusMessage('Arama başlatılıyor…');
      await acquireLocalMic();

      const session = ua.call(target, {
        mediaConstraints: { audio: true, video: false },
        pcConfig: pcConfigRef.current,
        rtcOfferConstraints: { offerToReceiveAudio: true, offerToReceiveVideo: false },
      });

      if (session && sessionRef.current !== session) {
        handleOutgoingSession(session);
      }

      return { ok: true };
    } catch (err) {
      setIsDialing(false);
      outboundTelefonRef.current = null;
      const mesaj = err?.message || 'Arama başlatılamadı';
      return hataDon(mesaj);
    }
  }, [acquireLocalMic, bilgilendirSantral, handleOutgoingSession]);

  useEffect(() => {
    mountedRef.current = true;
    const onBeforeUnload = () => stopSoftphone();
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
      mountedRef.current = false;
      // Sayfa içi route değişiminde provider artık App seviyesinde; yalnızca gerçek unmount'ta kes
      setTimeout(() => {
        if (!mountedRef.current) stopSoftphone();
      }, 0);
    };
  }, [stopSoftphone]);

  const answerCall = useCallback(async () => {
    const session = sessionRef.current;
    if (!session || isAnsweringRef.current) return;

    isAnsweringRef.current = true;
    setIsAnswering(true);
    ringtone.stop();
    setConnectionState('answering');
    setStatusMessage('Mikrofon hazırlanıyor…');

    if (answerTimeoutRef.current) {
      clearTimeout(answerTimeoutRef.current);
    }
    answerTimeoutRef.current = setTimeout(() => {
      if (!isAnsweringRef.current) return;
      setStatusMessage('Cevaplama uzun sürüyor. Bağlantı kuruluyor olabilir; bekleyin veya reddedin.');
    }, 15000);

    try {
      const stream = await acquireLocalMic();
      ensureMicTracksEnabled(stream);
      setStatusMessage('Arama cevaplanıyor…');
      session.answer(getSessionAnswerOptions(stream));
    } catch (err) {
      resetAnswering();
      setConnectionState('incoming');
      if (err?.name === 'INVALID_STATE_ERROR') {
        setStatusMessage('Arama zaten cevaplanıyor veya sonlandı.');
      } else {
        setStatusMessage(err?.message || 'Arama cevaplanamadı');
        setConnectionState('error');
      }
    }
  }, [acquireLocalMic, getSessionAnswerOptions, resetAnswering, ringtone]);

  /** Kişisel red — çağrı kuyrukta kalır / başka dahiliye yönlenebilir (603 Decline). */
  const rejectCall = useCallback(() => {
    const session = sessionRef.current;
    if (!session) return;
    ringtone.stop();
    try {
      session.terminate({ status_code: 603, reason_phrase: 'Decline' });
    } catch {
      /* ignore */
    }
    clearSession();
    setConnectionState(uaRef.current?.isRegistered?.() ? 'registered' : 'idle');
  }, [clearSession, ringtone]);

  /**
   * Aramayı tamamen sonlandır — Netgsm hangup + yerel SIP kapatma.
   * @returns {Promise<{ok: boolean, mesaj?: string}>}
   */
  const cancelIncomingCall = useCallback(async () => {
    ringtone.stop();
    const session = sessionRef.current;
    const uniqueId = incomingCall?.uniqueId || extractNetgsmUniqueId(session);

    let netgsmOk = false;
    if (uniqueId) {
      try {
        const res = await netgsmCagriSonlandir(uniqueId);
        netgsmOk = res?.basarili === true;
        if (!netgsmOk) {
          const mesaj = res?.mesaj || 'Netgsm çağrı sonlandırma başarısız';
          if (typeof window !== 'undefined' && window.showNotification) {
            window.showNotification('warning', mesaj);
          }
        }
      } catch (err) {
        const mesaj = err?.message || 'Netgsm çağrı sonlandırma başarısız';
        if (typeof window !== 'undefined' && window.showNotification) {
          window.showNotification('warning', mesaj);
        }
      }
    }

    if (session) {
      try {
        session.terminate({ status_code: 487, reason_phrase: 'Request Terminated' });
      } catch {
        /* ignore */
      }
    }

    clearSession();
    setConnectionState(uaRef.current?.isRegistered?.() ? 'registered' : 'idle');

    if (!uniqueId && !netgsmOk) {
      const mesaj = 'Çağrı kimliği (unique_id) alınamadı; yerel bağlantı kapatıldı. Arayan hattı açık kalabilir.';
      if (typeof window !== 'undefined' && window.showNotification) {
        window.showNotification('warning', mesaj);
      }
      return { ok: false, mesaj };
    }

    return { ok: netgsmOk || !!session, mesaj: netgsmOk ? 'Çağrı sonlandırıldı' : undefined };
  }, [clearSession, incomingCall?.uniqueId, ringtone]);

  const hangupCall = useCallback(() => {
    const session = sessionRef.current;
    if (!session) return;
    session.terminate();
    clearSession();
    setConnectionState(uaRef.current?.isRegistered?.() ? 'registered' : 'idle');
  }, [clearSession]);

  /** Görüşme sırasında yerel mikrofonu susturur / açar (JsSIP mute). */
  const toggleMicMute = useCallback(() => {
    const session = sessionRef.current;
    if (!session?.isEstablished?.()) {
      setStatusMessage('Mikrofon susturma yalnızca aktif görüşmede kullanılabilir.');
      return;
    }

    try {
      const mutedState = session.isMuted?.();
      const audioMuted = mutedState?.audio === true;

      if (audioMuted) {
        session.unmute({ audio: true });
        setMicMuted(false);
        setStatusMessage('Mikrofon açıldı');
      } else {
        session.mute({ audio: true });
        setMicMuted(true);
        setStatusMessage('Mikrofon susturuldu — karşı taraf sizi duymaz');
      }
      return;
    } catch {
      /* JsSIP mute başarısız — track fallback */
    }

    const stream = localMediaStreamRef.current;
    const track = stream?.getAudioTracks?.()[0];
    if (!track) {
      setStatusMessage('Mikrofon akışı bulunamadı.');
      return;
    }
    track.enabled = !track.enabled;
    setMicMuted(!track.enabled);
    setStatusMessage(track.enabled ? 'Mikrofon açıldı' : 'Mikrofon susturuldu — karşı taraf sizi duymaz');
  }, []);

  /**
   * Aktif görüşmeyi başka bir dahiliye kör (blind) aktarır — SIP REFER.
   * @param {string} hedef - Hedef dahili numarası (ör. "102") veya dış numara
   */
  const transferCall = useCallback((hedef) => {
    const session = sessionRef.current;
    const target = String(hedef || '').trim();
    if (!session || !target) return;
    if (!session.isEstablished?.()) {
      setStatusMessage('Aktarma için görüşmenin aktif olması gerekir.');
      return;
    }
    if (isTransferring) return;

    setIsTransferring(true);
    setStatusMessage(`Çağrı ${target} dahilisine aktarılıyor…`);

    try {
      session.refer(target, {
        eventHandlers: {
          requestSucceeded: () => {
            setStatusMessage(`Aktarma isteği kabul edildi (${target}).`);
          },
          requestFailed: (e) => {
            setIsTransferring(false);
            setStatusMessage(`Aktarma reddedildi: ${e?.cause || 'bilinmeyen sebep'}`);
          },
          accepted: () => {
            setStatusMessage(`Çağrı ${target} dahilisine aktarıldı.`);
          },
          failed: (e) => {
            setIsTransferring(false);
            setStatusMessage(`Aktarma başarısız: ${e?.cause || 'bilinmeyen sebep'}`);
          },
        },
      });
    } catch (err) {
      setIsTransferring(false);
      setStatusMessage(err?.message || 'Aktarma sırasında hata oluştu.');
    }
  }, [isTransferring]);

  const isRegistered = connectionState === 'registered' || connectionState === 'incoming' || connectionState === 'answering' || connectionState === 'active';
  const hasIncoming = connectionState === 'incoming' || connectionState === 'answering';
  const hasActive = connectionState === 'active' && !!activeCall;
  const hasSipSession = !!incomingCall?.session;

  return {
    connectionState,
    statusMessage,
    extensionLabel,
    incomingCall,
    activeCall,
    isRegistered,
    hasIncoming,
    hasActive,
    hasSipSession,
    isAnswering,
    isTransferring,
    isDialing,
    santralLoglari,
    gelenCagriGecmisi,
    appendSantralLog,
    clearSantralLoglari,
    kaydetGelenCagri,
    micActive,
    micMuted,
    remoteAudioRef,
    ringtoneAudioRef,
    reattachRemoteAudio,
    startSoftphone,
    stopSoftphone,
    answerCall,
    rejectCall,
    cancelIncomingCall,
    hangupCall,
    dialCall,
    toggleMicMute,
    transferCall,
    startRingtone: ringtone.start,
    stopRingtone: ringtone.stop,
    setOnCallAccepted,
    setOnCallEnded,
  };
}

export default useNetgsmSoftphone;
